import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Button,
  Space,
  Descriptions,
  Empty,
  Spin,
  Alert,
  message,
} from 'antd';
import { useParams, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  UserOutlined,
  CalendarOutlined,
  FileTextOutlined,
  DownloadOutlined,
  ArrowLeftOutlined,
  PhoneOutlined,
} from '@ant-design/icons';
import Api from '../../Network';
import { usePermissions } from '../../contexts/PermissionContext';
import MobileTable from '../../Component/MobileTable';

const { Title, Text } = Typography;

type ReceiptDetailProps = Record<string, never>;

const ReceiptDetail: React.FC<ReceiptDetailProps> = () => {
  const { t } = useTranslation();
  const { hasPermission, hasAnyPermission } = usePermissions();
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const [receipt, setReceipt] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch receipt details
  const fetchReceiptDetails = async (receiptId: string) => {
    setLoading(true);
    setError(null);
    try {
      const response: any = await Api.get(`/api/admin/receipts/${receiptId}`);
      if (response.status && response.data) {
        setReceipt(response.data);
      } else {
        setError(t('receipts.messages.noReceiptFound', 'Receipt not found'));
      }
    } catch (error) {
      message.error(t('receipts.messages.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchReceiptDetails(id);
    }
  }, [id]);

  const handleBack = () => {
    history.goBack();
  };

  const getStatusColor = (status: string) => {
    const statusColors: { [key: string]: string } = {
      paid: 'green',
      unpaid: 'orange',
      cancelled: 'red',
      pending: 'blue',
    };
    return statusColors[status.toLowerCase()] || 'default';
  };

  // Handle download receipt
  const handleDownloadReceipt = async () => {
    if (!receipt?.id) return;

    try {
      // Try to get the PDF from the storage path
      const response = await fetch(
        `https://driveshield.net/storage/receipts/pdf/${receipt.id}/receipt.pdf`
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `receipt_${receipt.reference || receipt.id}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        // If direct access fails, try to open in new tab as fallback
        window.open(
          `https://driveshield.net/storage/receipts/pdf/${receipt.id}/receipt.pdf`,
          '_blank'
        );
      }
    } catch (error) {
      message.error(t('receipts.messages.downloadFailed'));
    }
  };

  // Handle preview receipt
  const handlePreviewReceipt = async () => {
    if (!receipt?.id) return;

    try {
      // Try to get the PDF from the storage path
      const response = await fetch(
        `https://driveshield.net/storage/receipts/pdf/${receipt.id}/receipt.pdf`
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
      } else {
        // If direct access fails, try to open in new tab as fallback
        window.open(
          `https://driveshield.net/storage/receipts/pdf/${receipt.id}/receipt.pdf`,
          '_blank'
        );
      }
    } catch (error) {
      message.error(t('receipts.messages.previewFailed'));
    }
  };

  if (loading) {
    return (
      <div className="receipt-detail-page" style={{ padding: '24px' }}>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
          <div style={{ marginTop: '16px' }}>{t('common.loading')}</div>
        </div>
      </div>
    );
  }

  if (error || !receipt) {
    return (
      <div className="receipt-detail-page" style={{ padding: '24px' }}>
        <Alert
          message={t('common.error')}
          description={error || t('receipts.messages.noReceiptFound')}
          type="error"
          showIcon
          style={{ margin: '24px' }}
        />
      </div>
    );
  }

  const receiptItemsColumns = [
    {
      title: t('receipts.columns.description', 'Description'),
      dataIndex: 'description',
      key: 'description',
      render: (description: string) => <Text>{description || 'N/A'}</Text>,
    },
    {
      title: t('receipts.columns.quantity', 'Quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
      width: 120,
      render: (quantity: string) => (
        <Text strong className="quantity-text">
          {parseFloat(quantity || '0').toFixed(2)}
        </Text>
      ),
    },
    {
      title: t('receipts.columns.unitPrice', 'Unit Price'),
      dataIndex: 'unit_price',
      key: 'unit_price',
      width: 140,
      render: (price: string) => (
        <Text className="price-text">
          {parseFloat(price || '0').toFixed(2)} {t('common.SAR')}
        </Text>
      ),
    },
    {
      title: t('receipts.columns.total', 'Total'),
      dataIndex: 'total',
      key: 'total',
      width: 120,
      render: (total: number) => (
        <Text strong className="total-text">
          {total.toFixed(2)} {t('common.SAR')}
        </Text>
      ),
    },
  ];

  // Columns for allocated details table
  const allocatedDetailsColumns = [
    {
      title: t('receipts.details.allocatedDetails.date', 'Date'),
      dataIndex: 'date',
      key: 'date',
      // width: 120,
      render: (date: string) => <Text>{new Date(date).toLocaleDateString()}</Text>,
    },
    {
      title: t('receipts.details.allocatedDetails.invoiceReference', 'Invoice Reference'),
      dataIndex: 'allocatee_id',
      key: 'invoiceReference',
      render: (allocateeId: number, record: any) => {
        // Try to get the invoice reference from the allocatee relationship
        // allocatee can be an array or a single object
        const allocatee = Array.isArray(record.allocatee) ? record.allocatee[0] : record.allocatee;
        const invoice = allocatee;
        return (
          <Text code strong>
            {invoice?.reference || `Invoice #${allocateeId}` || 'N/A'}
          </Text>
        );
      },
    },
    // {
    //   title: t('receipts.details.allocatedDetails.invoiceTotal', 'Invoice Total'),
    //   dataIndex: 'allocatee_id',
    //   key: 'invoiceTotal',
    //   width: 140,
    //   render: (allocateeId: number, record: any) => {
    //     const invoice = record.allocatee;
    //     return (
    //       <Text strong>
    //         {invoice?.total ? parseFloat(String(invoice.total)).toFixed(2) : '0.00'} {t('common.SAR')}
    //       </Text>
    //     );
    //   },
    // },
    // {
    //   title: t('receipts.details.allocatedDetails.dueAmount', 'Due Amount'),
    //   dataIndex: 'allocatee_id',
    //   key: 'dueAmount',
    //   width: 140,
    //   render: (allocateeId: number, record: any) => {
    //     const invoice = record.allocatee;
    //     return (
    //       <Text type="danger">
    //         {invoice?.due_amount ? parseFloat(String(invoice.due_amount)).toFixed(2) : '0.00'} {t('common.SAR')}
    //       </Text>
    //     );
    //   },
    // },
    {
      title: t('receipts.details.allocatedDetails.allocatedAmount', 'Allocated Amount'),
      dataIndex: 'amount',
      key: 'allocatedAmount',
      // width: 140,
      render: (amount: number) => (
        <Text strong style={{ color: '#52c41a' }}>
          {parseFloat(String(amount || '0')).toFixed(2)} {t('common.SAR')}
        </Text>
      ),
    },
    // {
    //   title: t('receipts.details.allocatedDetails.actions', 'Actions'),
    //   key: 'actions',
    //   width: 100,
    //   render: (_: any, record: any) => {
    //     const invoice = record.allocatee;
    //     return (
    //       <Button
    //         type="link"
    //         size="small"
    //         icon={<EyeOutlined />}
    //         onClick={() => invoice?.id && history.push(`/invoices/${invoice.id}`)}
    //         disabled={!invoice?.id}
    //       >
    //         {t('common.view', 'View')}
    //       </Button>
    //     );
    //   },
    // },
  ];

  return (
    <div className="receipt-detail-page" style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <Button icon={<ArrowLeftOutlined />} onClick={handleBack} style={{ marginBottom: 16 }}>
          {t('common.back')}
        </Button>

        <Title level={2} style={{ margin: 0 }}>
          {t('receipts.details.title', 'Receipt Details')} - {receipt.reference}
        </Title>
      </div>

      {/* Receipt Header */}
      <Card className="receipt-header-card" style={{ marginBottom: '24px' }}>
        <Row gutter={[24, 16]}>
          <Col xs={24} sm={12}>
            <Descriptions column={1} size="small" className="receipt-info">
              <Descriptions.Item label={t('receipts.columns.reference', 'Reference')}>
                <Text code strong className="reference-text">
                  {receipt.reference}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label={t('receipts.columns.status', 'Status')}>
                <Tag color={getStatusColor(receipt.kind || receipt.status)} className="status-tag">
                  {receipt.kind || receipt.status}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('receipts.columns.issueDate', 'Issue Date')}>
                <div className="date-info">
                  <CalendarOutlined />{' '}
                  {new Date(receipt.date || receipt.created_at).toLocaleDateString()}
                </div>
              </Descriptions.Item>
            </Descriptions>
          </Col>
          <Col xs={24} sm={12}>
            <Descriptions column={1} size="small" className="receipt-info">
              <Descriptions.Item label={t('receipts.columns.customer', 'Customer')}>
                <div className="customer-info">
                  <UserOutlined /> {receipt.contact?.name || receipt.customer?.name || 'N/A'}
                </div>
              </Descriptions.Item>
              {(receipt.contact?.phone_number || receipt.customer?.phone_number) && (
                <Descriptions.Item label={t('receipts.columns.phone', 'Phone')}>
                  <div className="phone-info">
                    <PhoneOutlined />{' '}
                    {receipt.contact?.phone_number || receipt.customer?.phone_number}
                  </div>
                </Descriptions.Item>
              )}
              <Descriptions.Item label={t('receipts.columns.amount', 'Amount')}>
                <Text strong style={{ fontSize: '16px', color: '#1890ff' }}>
                  {parseFloat(receipt.amount || receipt.total || '0').toFixed(2)} {t('common.SAR')}
                </Text>
              </Descriptions.Item>
            </Descriptions>
          </Col>
        </Row>
      </Card>

      {/* Receipt Items */}
      {receipt.items && receipt.items.length > 0 && (
        <Card
          title={t('receipts.details.items', 'Receipt Items')}
          className="receipt-items-card"
          style={{ marginBottom: '24px' }}
        >
          <MobileTable
            columns={receiptItemsColumns}
            dataSource={receipt.items}
            rowKey="id"
            pagination={false}
            size="small"
            className="receipt-items-table"
            scroll={{ x: 600 }}
          />
        </Card>
      )}

      {/* Allocated Details */}
      {receipt.allocates && receipt.allocates.length > 0 ? (
        <Card
          title={t('receipts.details.allocatedDetails.title', 'Allocated Details')}
          className="allocated-details-card"
          style={{ marginBottom: '24px' }}
        >
          <MobileTable
            columns={allocatedDetailsColumns}
            dataSource={receipt.allocates}
            rowKey="id"
            pagination={false}
            size="small"
            className="allocated-details-table"
            scroll={{ x: 600 }}
            locale={{
              emptyText: t(
                'receipts.details.allocatedDetails.noData',
                'No allocated details found'
              ),
            }}
          />
        </Card>
      ) : (
        <Card
          title={t('receipts.details.allocatedDetails.title', 'Allocated Details')}
          className="allocated-details-card"
          style={{ marginBottom: '24px' }}
        >
          <Empty
            description={t(
              'receipts.details.allocatedDetails.noData',
              'No allocated details found'
            )}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </Card>
      )}

      {/* Receipt Summary */}
      <Card
        title={t('receipts.details.summary', 'Receipt Summary')}
        style={{ marginBottom: '24px' }}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8}>
            <Card size="small">
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff' }}>
                  {parseFloat(receipt.amount || receipt.total || '0').toFixed(2)} {t('common.SAR')}
                </div>
                <div style={{ color: 'var(--color-text-secondary)' }}>
                  {t('receipts.details.totalAmount', 'Total Amount')}
                </div>
              </div>
            </Card>
          </Col>
        </Row>
      </Card>

      {/* Actions */}
      {(hasPermission('download_receipt') ||
        hasPermission('receipts.download') ||
        hasPermission('export_receipts') ||
        hasAnyPermission(['view_receipts', 'view_receipt', 'receipts.read', 'receipts.view'])) && (
        <Card title={t('receipts.details.actions', 'Actions')} style={{ marginBottom: '24px' }}>
          <Space>
            {(hasPermission('download_receipt') ||
              hasPermission('receipts.download') ||
              hasPermission('export_receipts')) && (
              <Button type="primary" icon={<DownloadOutlined />} onClick={handleDownloadReceipt}>
                {t('receipts.details.download', 'Download Receipt')}
              </Button>
            )}
            {(hasPermission('view_receipts') ||
              hasPermission('view_receipt') ||
              hasPermission('receipts.read') ||
              hasPermission('receipts.view')) && (
              <Button icon={<FileTextOutlined />} onClick={handlePreviewReceipt}>
                {t('receipts.details.preview', 'Preview Receipt')}
              </Button>
            )}
          </Space>
        </Card>
      )}
    </div>
  );
};

export default ReceiptDetail;
